import React from 'react';
import { Handle, Position } from '@xyflow/react';
import type { Port } from '../graphTypes';
import { acceptedKindsOf, acceptsAnyKind } from '../graphTypes';
import { EdgeDotIn, EdgePillCount, EdgePillOut, PortLayer } from './PortHandles.styled';

export interface PortHandlesProps {
  readonly ports: readonly Port[] | undefined;
  readonly nodeId: string;
  /**
   * Output ports owned by a sibling renderer (e.g. {@code BranchRow}
   * mounts its own per-branch handles). When true, this component
   * skips output handles and only renders input handles. Avoids
   * duplicate handles competing for the same port id.
   */
  readonly skipOutputs?: boolean;
  /**
   * When >0, renders a SINGLE collapsed edge pill that contains ALL
   * output handles (so React Flow can still route edges) plus a count
   * badge showing the number. Used when a multi-output node is
   * collapsed — the per-row OutputRow is hidden but we still need the
   * handles mounted and a visual pill for the user.
   */
  readonly collapsedOutputCount?: number;
}

const CHEVRON_SVG = (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M9 6l6 6-6 6" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

/**
 * Port handles with design-accurate chrome: input ports render as a
 * small solid disc on the left edge, output ports as a capsule pill on
 * the right edge with a chevron glyph. The React Flow {@code <Handle>}
 * sits *inside* the chrome element (transparent, stretched to fill) so
 * it's the Hit target while the styled wrapper provides the visual.
 *
 * When {@code collapsedOutputCount} > 0 the component renders all
 * output handles inside a single pill with a count badge — this is the
 * collapsed-state affordance for multi-output nodes.
 */
const PortHandles: React.FC<PortHandlesProps> = ({
  ports,
  nodeId,
  skipOutputs,
  collapsedOutputCount = 0,
}) => {
  if (!Array.isArray(ports) || ports.length === 0) return null;
  const inputs = ports.filter((p) => p.direction === 'input');
  const allOutputs = ports.filter((p) => p.direction === 'output');
  const showCollapsedPill = collapsedOutputCount > 1;
  const outputs = skipOutputs ? [] : allOutputs;
  return (
    <PortLayer>
      {inputs.map((p, i) => {
        const kinds = acceptedKindsOf(p);
        // A catch-all port reads as "Any type": listing all seven kinds would
        // look like a narrowing, and the multi-type chrome implies a choice.
        const anyKind = acceptsAnyKind(kinds);
        const tooltip = anyKind
          ? `${p.label} \u2014 Any type`
          : `${p.label} \u2014 ${kinds.join(', ') || p.portType}`;
        return (
          <EdgeDotIn
            key={p.portId}
            title={tooltip}
            data-multi-type={(!anyKind && kinds.length > 1) || undefined}
            data-port-kinds={kinds.join(',')}
          >
            <Handle
              type="target"
              position={Position.Left}
              id={p.portId}
              data-testid={
                i === 0
                  ? `process-node__handle-in-${nodeId}`
                  : `process-node__handle-in-${nodeId}-${i}`
              }
            />
          </EdgeDotIn>
        );
      })}
      {showCollapsedPill ? (
        <EdgePillOut>
          {CHEVRON_SVG}
          <EdgePillCount>{collapsedOutputCount}</EdgePillCount>
          {allOutputs.map((p, i) => (
            <Handle
              key={p.portId}
              type="source"
              position={Position.Right}
              id={p.portId}
              isConnectable={false}
              data-testid={
                i === 0
                  ? `process-node__handle-out-${nodeId}`
                  : `process-node__handle-out-${nodeId}-${i}`
              }
            />
          ))}
        </EdgePillOut>
      ) : (
        outputs.map((p, i) => (
          <EdgePillOut key={p.portId}>
            {CHEVRON_SVG}
            <Handle
              type="source"
              position={Position.Right}
              id={p.portId}
              data-testid={
                i === 0
                  ? `process-node__handle-out-${nodeId}`
                  : `process-node__handle-out-${nodeId}-${i}`
              }
            />
          </EdgePillOut>
        ))
      )}
    </PortLayer>
  );
};

export default PortHandles;
